// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * Help Plans Translation Schema
 * @file help.plans.schema.js
 * @description Schema único e inmutable con TODAS las claves obligatorias para la sección de planes
 * @version v3.3.0
 * 
 * ESTE ES EL CONTRATO ÚNICO PARA TODOS LOS IDIOMAS
 * Cualquier cambio aquí debe reflejarse en los 18 idiomas
 */

/**
 * Array inmutable con TODAS las claves obligatorias de la sección de planes
 * Orden: Títulos → FREE → PRO → ENTERPRISE (oculto pero preservado) → Upgrade
 * NOTA: Enterprise está oculto visualmente pero las claves se mantienen para compatibilidad
 */
export const HELP_PLANS_REQUIRED_KEYS = Object.freeze([
  // Títulos y descripción general
  'help.plans.title',
  'help.plans.intro',
  
  // Plan FREE - Títulos y descripción
  'help.plans.free.title',
  'help.plans.free.description',
  'help.plans.free.limits',
  
  // Plan FREE - Puntos (7 puntos)
  'help.plans.free.point1',
  'help.plans.free.point2',
  'help.plans.free.point3',
  'help.plans.free.point4',
  'help.plans.free.point5',
  'help.plans.free.point6',
  'help.plans.free.point7',
  'help.plans.free.devices',
  
  // Plan PRO - Títulos y descripción
  'help.plans.pro.title',
  'help.plans.pro.description',
  'help.plans.pro.price',
  'help.plans.pro.benefits',
  
  // Plan PRO - Puntos (10 puntos)
  'help.plans.pro.point1',
  'help.plans.pro.point2',
  'help.plans.pro.point3',
  'help.plans.pro.point4',
  'help.plans.pro.point5',
  'help.plans.pro.point6',
  'help.plans.pro.point7',
  'help.plans.pro.point8',
  'help.plans.pro.point9',
  'help.plans.pro.point10',
  'help.plans.pro.devices',
  
  // Plan ENTERPRISE - Títulos y descripción
  'help.plans.enterprise.title',
  'help.plans.enterprise.description',
  'help.plans.urgent_slots_badge',
  'help.plans.enterprise.benefits',
  
  // Plan ENTERPRISE - Puntos (9 puntos)
  'help.plans.enterprise.point1',
  'help.plans.enterprise.point2',
  'help.plans.enterprise.point3',
  'help.plans.enterprise.point4',
  'help.plans.enterprise.point5',
  'help.plans.enterprise.point6',
  'help.plans.enterprise.point7',
  'help.plans.enterprise.point8',
  'help.plans.enterprise.point9',
  'help.plans.enterprise.devices',
  'help.plans.foundation.devices',
  
  // Upgrade CTA
  'help.plans.pro.upgrade'
]);

/**
 * Total de claves requeridas (incl. help.plans.foundation.devices para FOUNDATION=5 dispositivos)
 * NOTA: Enterprise y Foundation comparten límite 5; foundation.devices es la clave para la tarjeta FOUNDATION.
 */
export const HELP_PLANS_REQUIRED_KEYS_COUNT = HELP_PLANS_REQUIRED_KEYS.length;

/** Claves que pueden estar vacías a propósito (precio oculto en UI). */
export const HELP_PLANS_KEYS_ALLOW_EMPTY = Object.freeze([
  'help.plans.pro.price',
  'help.plans.enterprise.description',
  'help.plans.pro.devices',
  'help.plans.enterprise.devices',
  'help.plans.foundation.devices',
  'help.plans.enterprise.point9',
  'help.plans.pro.point9',
  'help.plans.pro.point10',
]);

/**
 * Valida que un array de claves coincida exactamente con el schema
 * @param {string[]} keys - Array de claves a validar
 * @returns {boolean} - true si coincide exactamente
 */
export const validateKeysMatchSchema = (keys) => {
  if (!Array.isArray(keys)) return false;
  if (keys.length !== HELP_PLANS_REQUIRED_KEYS.length) return false;
  
  const schemaSet = new Set(HELP_PLANS_REQUIRED_KEYS);
  const keysSet = new Set(keys);
  
  // Verificar que todas las claves del schema estén presentes
  for (const key of HELP_PLANS_REQUIRED_KEYS) {
    if (!keysSet.has(key)) return false;
  }
  
  // Verificar que no haya claves extra
  for (const key of keys) {
    if (!schemaSet.has(key)) return false;
  }
  
  return true;
};

/**
 * Obtiene las claves faltantes comparando un objeto de traducciones con el schema
 * @param {Object} translations - Objeto de traducciones
 * @returns {string[]} - Array de claves faltantes
 */
export const getMissingKeys = (translations) => {
  if (!translations || typeof translations !== 'object') {
    return [...HELP_PLANS_REQUIRED_KEYS];
  }
  
  const missing = [];
  for (const key of HELP_PLANS_REQUIRED_KEYS) {
    if (!(key in translations)) {
      missing.push(key);
    }
  }
  
  return missing;
};

/**
 * Obtiene las claves extra (no definidas en el schema)
 * @param {Object} translations - Objeto de traducciones
 * @returns {string[]} - Array de claves extra
 */
export const getExtraKeys = (translations) => {
  if (!translations || typeof translations !== 'object') {
    return [];
  }
  
  const schemaSet = new Set(HELP_PLANS_REQUIRED_KEYS);
  const extra = [];
  
  for (const key of Object.keys(translations)) {
    if (key.startsWith('help.plans.') && !schemaSet.has(key)) {
      extra.push(key);
    }
  }
  
  return extra;
};

/**
 * Obtiene las claves vacías o inválidas
 * @param {Object} translations - Objeto de traducciones
 * @returns {string[]} - Array de claves vacías
 */
export const getEmptyKeys = (translations) => {
  if (!translations || typeof translations !== 'object') {
    return [];
  }
  
  const allowEmpty = new Set(HELP_PLANS_KEYS_ALLOW_EMPTY);
  const empty = [];
  for (const key of HELP_PLANS_REQUIRED_KEYS) {
    if (allowEmpty.has(key)) continue;
    if (key in translations) {
      const value = translations[key];
      if (!value || typeof value !== 'string' || value.trim() === '') {
        empty.push(key);
      }
    }
  }
  
  return empty;
};
